import { notification } from "antd";
import { useCallback } from "react";
import { postOrderSaveThunk } from "tm-shared/api";
import {
    CheckoutOrder,
    ProductOrderItem,
    ProductOrderItemDetails,
} from "tm-shared/types";
import { arrayToRecord } from "tm-shared/utils";

import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import {
    deductInventory,
    upsertSaleOrderUpdateRequestQueueItem,
} from "@/store/slices/offline";
import { setCheckoutProducts } from "@/store/slices/work";

const transformCheckoutToApiFormat = (
    checkout: CheckoutOrder,
    ignoreRemainings: string[] = [],
    localProducts: Record<string, ProductOrderItem>,
) => {
    const checkoutProducts = checkout.products ?? {};

    const allBarcodes = new Set<string>([
        ...Object.keys(checkoutProducts),
        ...Object.keys(localProducts),
    ]);

    const apiProducts: ProductOrderItemDetails[] = [];

    for (const barcode of allBarcodes) {
        const fromCheckout = checkoutProducts[barcode];
        const fromLocal = localProducts[barcode];

        const finalCount = fromLocal?.count ?? 0;
        const name = fromLocal?.name ?? fromCheckout?.name ?? "";
        const price = fromLocal?.price ?? fromCheckout?.price ?? 0;

        apiProducts.push({
            barcode,
            name,
            price,
            count: finalCount,
            apiCount: finalCount,
        });
    }

    return {
        marketProducts: { [checkout.orderId]: apiProducts },
        ignoreRemainingsOrders: ignoreRemainings.length
            ? { [checkout.orderId]: { ignoreRemainings: true } }
            : {},
        paymentForms: { [checkout.orderId]: checkout.paymentForm.toString() },
    };
};

const buildInventoryDelta = (
    checkout: CheckoutOrder,
    apiProducts: ProductOrderItemDetails[],
): Record<string, number> => {
    const checkoutProducts = checkout.products ?? {};

    const newMap: Record<string, number> = {};
    for (const p of apiProducts) newMap[p.barcode] = p.count ?? 0;

    const allBarcodes = new Set<string>([
        ...Object.keys(checkoutProducts),
        ...Object.keys(newMap),
    ]);

    const deltaMap: Record<string, number> = {};

    for (const barcode of allBarcodes) {
        const oldCount = checkoutProducts[barcode]?.count ?? 0;

        const newCount = newMap[barcode] ?? 0;

        const delta = newCount - oldCount;

        if (delta !== 0) deltaMap[barcode] = delta;
    }

    return deltaMap;
};

export const useSaveOrder = () => {
    const dispatch = useAppDispatch();

    const { offlineMode, ignoreRemainingsOrders } = useAppSelector(
        (s) => s.offline,
    );

    const { checkoutList } = useAppSelector((state) => state.work);

    const handleSaveOrder = useCallback(
        async ({
            orderId,
            products,
            storageId,
        }: {
            orderId: string;
            products: Record<string, ProductOrderItem>;
            storageId: string;
        }) => {
            if (!orderId || !checkoutList?.[orderId]) return;

            try {
                const checkout = checkoutList[orderId];
                const ignoreList = ignoreRemainingsOrders?.[orderId] || [];

                const apiFormat = transformCheckoutToApiFormat(
                    checkout,
                    ignoreList,
                    products,
                );

                if (offlineMode) {
                    dispatch(
                        upsertSaleOrderUpdateRequestQueueItem({
                            orderId,
                            orderPayload: apiFormat,
                        }),
                    );
                } else {
                    await dispatch(postOrderSaveThunk(apiFormat)).unwrap();
                }

                const updatedProducts = apiFormat.marketProducts[
                    orderId
                ].filter((p) => p.count !== 0);

                const updatedProductMap = arrayToRecord(
                    updatedProducts,
                    "barcode",
                );

                dispatch(
                    setCheckoutProducts({
                        orderId,
                        products: updatedProductMap,
                    }),
                );

                const apiProducts = apiFormat.marketProducts[orderId];

                const inventoryDelta = buildInventoryDelta(
                    checkout,
                    apiProducts,
                );

                dispatch(
                    deductInventory({
                        storageId,
                        newInventory: inventoryDelta,
                    }),
                );

                notification.success({ message: "Замовлення оновлено" });
            } catch (error) {
                notification.error({
                    message: "Помилка при збереженні замовлення",
                    description:
                        error instanceof Error
                            ? error.message
                            : "Невідома помилка",
                });
            }
        },
        [checkoutList, dispatch, ignoreRemainingsOrders, offlineMode],
    );

    return { handleSaveOrder };
};
